/** Facing direction with lerp smoothing, head swivel sweeps, and wall-avoidance idle scan. */
import { TILE_SIZE } from '../utils/Constants';
import { worldToTile, magnitude, lerp, degreesToRadians, TAU, rotate2D, directionToAngle } from '../utils/Math';
import type { AIState, DutySubstate } from './constants';
import {
  FACING_LERP_SPEED, WALL_FACE_CHECK_DIST,
  IDLE_WALL_CHECK_DELAY_MS,
  IDLE_LOOK_SWEEP_DEG, IDLE_LOOK_PERIOD_MS,
  SEARCH_LOOK_SWEEP_DEG, SEARCH_LOOK_PERIOD_MS,
  ALERT_LOOK_SWEEP_DEG, ALERT_LOOK_PERIOD_MS,
  PASSIVE_LOOK_SWEEP_DEG, PASSIVE_LOOK_PERIOD_MS,
  tileBlocksMovementStrict,
} from './constants';

/**
 * Manages enemy facing direction: lerp smoothing, head swivel sweeps,
 * idle look-around, and wall-avoidance.
 */
export class Facing {
  private _facingX: number = 0;
  private _facingY: number = 1; // default: facing south

  // Sweep timers (one per awareness level)
  private idleLookTimer: number = 0;
  private searchLookTimer: number = 0;
  private alertLookTimer: number = 0;

  // Sweep base direction (lerped during movement, snapped when stationary)
  private sweepBaseFX: number = 0;
  private sweepBaseFY: number = 1;
  private sweepBaseSet: boolean = false;

  // Settling delay — prevents wall-avoidance from firing immediately when stopping
  private stationaryTimer: number = 0;
  // Once wall-avoidance has run (or determined no wall), don't re-check until next move
  private wallCheckDone: boolean = false;

  private readonly groundLayer: Phaser.Tilemaps.TilemapLayer;

  constructor(groundLayer: Phaser.Tilemaps.TilemapLayer) {
    this.groundLayer = groundLayer;
  }

  get facingX(): number { return this._facingX; }
  get facingY(): number { return this._facingY; }

  /** Set facing to an arbitrary unit vector (used for spawn randomization). */
  setFacing(fx: number, fy: number): void {
    this._facingX = fx;
    this._facingY = fy;
    this.sweepBaseFX = fx;
    this.sweepBaseFY = fy;
  }

  /** Snap facing to a direction immediately (used by spikeAggro). */
  snapToward(targetX: number, targetY: number, fromX: number, fromY: number): void {
    const dx = targetX - fromX;
    const dy = targetY - fromY;
    const len = magnitude(dx, dy);
    if (len > 0.1) {
      this._facingX = dx / len;
      this._facingY = dy / len;
    }
  }

  /**
   * Update facing from movement velocity, state, and aggro.
   * Handles sweep oscillation for search/alert/duty+aggro, lerp smoothing
   * for chase/duty, and wall-avoidance idle scanning.
   */
  update(vx: number, vy: number, delta: number, entityX: number, entityY: number, state: AIState, aggro: number, dutySubstate?: DutySubstate): void {
    const lenSq = vx * vx + vy * vy;
    if (lenSq > 0.01) {
      this.stationaryTimer = 0; // Reset settling timer when moving
      this.wallCheckDone = false;
      const len = magnitude(vx, vy);
      const fx = vx / len;
      const fy = vy / len;

      const shouldSweep = state === 'search' || state === 'alert' || (state === 'duty' && aggro > 0);
      if (shouldSweep) {
        // Lerp sweep base toward movement direction (smooth out pathfinding turns)
        const t = Math.min(1, FACING_LERP_SPEED * (delta / 1000));
        let bx = lerp(this.sweepBaseFX, fx, t);
        let by = lerp(this.sweepBaseFY, fy, t);
        const bLen = magnitude(bx, by);
        if (bLen > 0.01) {
          this.sweepBaseFX = bx / bLen;
          this.sweepBaseFY = by / bLen;
        }
        this.sweepBaseSet = true;
        this.applySweep(this.sweepBaseFX, this.sweepBaseFY, delta, state);
      } else {
        // Not sweeping — reset sweep state, lerp toward movement direction
        this.idleLookTimer = 0;
        this.sweepBaseSet = false;
        const t = Math.min(1, FACING_LERP_SPEED * (delta / 1000));
        let newFX = lerp(this._facingX, fx, t);
        let newFY = lerp(this._facingY, fy, t);
        const nLen = magnitude(newFX, newFY);
        if (nLen > 0.01) {
          this._facingX = newFX / nLen;
          this._facingY = newFY / nLen;
        }
      }
    } else if (state === 'search' || state === 'alert' || (state === 'duty' && aggro > 0)) {
      // Stationary with awareness — sweep around stored base facing
      if (!this.sweepBaseSet) {
        this.sweepBaseFX = this._facingX;
        this.sweepBaseFY = this._facingY;
        this.sweepBaseSet = true;
      }
      this.applySweep(this.sweepBaseFX, this.sweepBaseFY, delta, state);
    } else if (state === 'duty') {
      this.updateIdleFacing(delta, entityX, entityY, dutySubstate);
    }
  }

  /** Apply sweep oscillation around a base direction. */
  private applySweep(baseFX: number, baseFY: number, delta: number, state: AIState): void {
    const isAlert = state === 'alert';
    const isSearch = state === 'search';
    const sweepDeg = isAlert ? ALERT_LOOK_SWEEP_DEG : SEARCH_LOOK_SWEEP_DEG;
    const period = isAlert ? ALERT_LOOK_PERIOD_MS : SEARCH_LOOK_PERIOD_MS;

    // Advance the appropriate timer
    if (isAlert) {
      this.alertLookTimer += delta;
    } else if (isSearch) {
      this.searchLookTimer += delta;
    } else {
      this.idleLookTimer += delta;
    }
    const timer = isAlert ? this.alertLookTimer : isSearch ? this.searchLookTimer : this.idleLookTimer;

    const sweepRad = degreesToRadians(sweepDeg);
    const angle = Math.sin((timer / period) * TAU) * sweepRad;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const rotated = rotate2D(baseFX, baseFY, cos, sin);
    this._facingX = rotated.x;
    this._facingY = rotated.y;
  }

  /** Stationary duty with no aggro: wall-avoidance + gentle idle scan. */
  private updateIdleFacing(delta: number, entityX: number, entityY: number, dutySubstate?: DutySubstate): void {
    this.stationaryTimer += delta;

    const checkX = entityX + this._facingX * WALL_FACE_CHECK_DIST;
    const checkY = entityY + this._facingY * WALL_FACE_CHECK_DIST;
    const tx = worldToTile(checkX);
    const ty = worldToTile(checkY);
    const tile = this.groundLayer.getTileAt(tx, ty);

    // Wall-avoidance: run once after settling delay, then never again until next move
    if (!this.wallCheckDone && this.stationaryTimer >= IDLE_WALL_CHECK_DELAY_MS) {
      this.wallCheckDone = true;

      if (tileBlocksMovementStrict(tile)) {
        // Facing a wall — find most open direction via symmetric raycast
        const facingAngle = directionToAngle(this._facingX, this._facingY);
        let bestAngle = facingAngle + Math.PI;
        let bestDist = 0;

        const offsets = [Math.PI / 4, -Math.PI / 4, Math.PI / 2, -Math.PI / 2,
                         Math.PI * 3 / 4, -Math.PI * 3 / 4, Math.PI];
        for (const off of offsets) {
          const angle = facingAngle + off;
          let dist = TILE_SIZE;
          while (dist <= WALL_FACE_CHECK_DIST) {
            const wx = entityX + Math.cos(angle) * dist;
            const wy = entityY + Math.sin(angle) * dist;
            const wTile = this.groundLayer.getTileAt(worldToTile(wx), worldToTile(wy));
            if (tileBlocksMovementStrict(wTile)) break;
            dist += TILE_SIZE;
          }
          if (dist > bestDist) {
            bestDist = dist;
            bestAngle = angle;
          }
        }

        // Set the sweep base to the wall-avoidance target direction
        this.sweepBaseFX = Math.cos(bestAngle);
        this.sweepBaseFY = Math.sin(bestAngle);
        this.sweepBaseSet = true;
        this._facingX = this.sweepBaseFX;
        this._facingY = this.sweepBaseFY;
        this.idleLookTimer = 0;
        // Fall through to idle sweep below
      }
    }

    // Idle sweep — width depends on duty substate
    if (!this.sweepBaseSet) {
      this.sweepBaseFX = this._facingX;
      this.sweepBaseFY = this._facingY;
      this.sweepBaseSet = true;
    }
    this.idleLookTimer += delta;
    const isPatrolling = dutySubstate === 'patrol' || dutySubstate === 'patrol-stalled';
    const sweepDeg = dutySubstate === 'passive' ? PASSIVE_LOOK_SWEEP_DEG
      : isPatrolling ? SEARCH_LOOK_SWEEP_DEG
      : IDLE_LOOK_SWEEP_DEG;
    const sweepPeriod = dutySubstate === 'passive' ? PASSIVE_LOOK_PERIOD_MS
      : isPatrolling ? SEARCH_LOOK_PERIOD_MS
      : IDLE_LOOK_PERIOD_MS;
    const sweepRad = degreesToRadians(sweepDeg);
    const angle = Math.sin((this.idleLookTimer / sweepPeriod) * TAU) * sweepRad;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const rotated = rotate2D(this.sweepBaseFX, this.sweepBaseFY, cos, sin);
    this._facingX = rotated.x;
    this._facingY = rotated.y;
  }
}
